import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    ArrowLeftOutlined,
    CloseOutlined,
    CloudDownloadOutlined,
    CloudSyncOutlined,
    DatabaseOutlined,
    DeleteOutlined,
    LoadingOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import {backupsAPI} from '@/api/endpoints/backups.js';
import {projectsAPI} from '@/api/endpoints/projects.js';
import styles from '@/styles/pages/backups/BackupsPage.module.css';

const formatSize = (bytes) => {
    if (bytes == null) return '';
    const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
    let value = bytes;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i++;
    }
    return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (iso) =>
    new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });

const formatEta = (sec) => {
    if (sec < 60) return `${sec} с`;
    if (sec < 3600) return `${Math.ceil(sec / 60)} мин`;
    return `${Math.floor(sec / 3600)} ч ${Math.ceil((sec % 3600) / 60)} мин`;
};

const UPLOADS_POLL_MS = 2000;

/**
 * /backups — бэкапы всех проектов; /projects/:uuid/backups — бэкапы одного проекта.
 */
const BackupsPage = () => {
    const {uuid} = useParams();
    const navigate = useNavigate();

    const [backups, setBackups] = useState([]);
    const [projectName, setProjectName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [uploads, setUploads] = useState([]);
    const [abortingId, setAbortingId] = useState(null);
    const prevUploads = useRef({}); // upload_id -> {offset, time, speed}: для расчёта скорости и обнаружения завершения

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);
        const response = uuid ? await backupsAPI.getByProject(uuid) : await backupsAPI.getAll();
        if (response.success) {
            setBackups(response.data || []);
        } else {
            setError(response.error || 'Не удалось получить бэкапы');
        }
        setLoading(false);
    }, [uuid]);

    useEffect(() => {
        load();
    }, [load]);

    // Опрос незавершённых загрузок: прогресс, скорость, обнаружение завершения
    useEffect(() => {
        let stopped = false;
        prevUploads.current = {};
        const poll = async () => {
            const response = await backupsAPI.getUploads();
            if (stopped || !response.success) return;
            const now = Date.now();
            const items = (response.data || [])
                .filter((u) => !uuid || u.project_uuid === uuid)
                .map((u) => {
                    const prev = prevUploads.current[u.upload_id];
                    let speed = prev?.speed || 0;
                    if (prev && now > prev.time) {
                        const delta = u.offset - prev.offset;
                        speed = delta > 0 ? delta / ((now - prev.time) / 1000) : 0;
                    }
                    return {...u, speed};
                });
            const finished = Object.keys(prevUploads.current).some((id) => !items.find((u) => u.upload_id === id));
            prevUploads.current = Object.fromEntries(
                items.map((u) => [u.upload_id, {offset: u.offset, time: now, speed: u.speed}])
            );
            setUploads(items);
            if (finished) load(true); // загрузка завершилась или отменена — обновляем таблицу
        };
        poll();
        const timer = setInterval(poll, UPLOADS_POLL_MS);
        return () => {
            stopped = true;
            clearInterval(timer);
        };
    }, [uuid, load]);

    const handleAbort = async (upload) => {
        if (!window.confirm(`Отменить загрузку «${upload.filename}»? Принятые данные (${formatSize(upload.offset)}) будут удалены.`)) {
            return;
        }
        setAbortingId(upload.upload_id);
        const response = await backupsAPI.abortUpload(upload.upload_id);
        setAbortingId(null);
        if (response.success) {
            setUploads((prev) => prev.filter((u) => u.upload_id !== upload.upload_id));
            delete prevUploads.current[upload.upload_id];
        }
    };

    useEffect(() => {
        if (!uuid) {
            setProjectName('');
            return;
        }
        projectsAPI.get(uuid, false).then((r) => r.success && setProjectName(r.data.name));
    }, [uuid]);

    useEffect(() => {
        document.title = projectName ? `Бэкапы ${projectName} | DevSpace` : 'Бэкапы | DevSpace';
    }, [projectName]);

    const handleDelete = async (backup) => {
        if (!window.confirm(`Удалить бэкап «${backup.filename}» (${formatSize(backup.size_bytes)})? Файл будет удалён с сервера.`)) {
            return;
        }
        setDeletingId(backup.id);
        const response = await backupsAPI.delete(backup.project_uuid, backup.id);
        setDeletingId(null);
        if (response.success) {
            setBackups((prev) => prev.filter((b) => b.id !== backup.id));
        }
    };

    const totalSize = useMemo(() => backups.reduce((sum, b) => sum + b.size_bytes, 0), [backups]);

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <button className={styles.backButton} onClick={() => navigate(-1)}>
                        <ArrowLeftOutlined/> Назад
                    </button>
                    <h1 className={styles.title}>
                        <DatabaseOutlined/> {uuid ? `Бэкапы: ${projectName || '...'}` : 'Бэкапы всех проектов'}
                    </h1>
                    <button className={styles.refreshButton} onClick={() => load()} disabled={loading}>
                        <ReloadOutlined spin={loading}/> Обновить
                    </button>
                </div>

                {uploads.length > 0 && (
                    <div className={styles.uploads}>
                        <h2 className={styles.uploadsTitle}>Загрузки ({uploads.length})</h2>
                        {uploads.map((u) => {
                            const percent = Math.min(100, Math.floor((u.offset / u.size) * 100));
                            const eta = u.speed > 0 ? Math.ceil((u.size - u.offset) / u.speed) : null;
                            return (
                                <div key={u.upload_id} className={styles.uploadCard}>
                                    <div className={styles.uploadTop}>
                                        <div>
                                            <span className={styles.filename}>{u.filename}</span>
                                            {!uuid && <span className={styles.uploadProject}> · {u.project_name}</span>}
                                        </div>
                                        <span className={`${styles.badge} ${u.stalled ? styles.badgeStalled : styles.badgeActive}`}>
                                            {u.stalled ? 'Остановлена' : 'Идёт загрузка'}
                                        </span>
                                        <button className={`${styles.iconButton} ${styles.danger}`} title="Отменить загрузку"
                                                disabled={abortingId === u.upload_id} onClick={() => handleAbort(u)}>
                                            {abortingId === u.upload_id ? <LoadingOutlined/> : <CloseOutlined/>}
                                        </button>
                                    </div>
                                    <div className={styles.progressTrack}>
                                        <div className={`${styles.progressBar} ${u.stalled ? styles.progressStalled : ''}`}
                                             style={{width: `${percent}%`}}/>
                                    </div>
                                    <div className={styles.uploadMeta}>
                                        <span>{percent}% · {formatSize(u.offset)} из {formatSize(u.size)}</span>
                                        {!u.stalled && u.speed > 0 && (
                                            <span>{formatSize(u.speed)}/с{eta ? ` · осталось ~${formatEta(eta)}` : ''}</span>
                                        )}
                                        {u.stalled && <span>Последний чанк: {formatDate(u.updated_at)}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!loading && !error && (
                    <p className={styles.summary}>
                        Бэкапов: {backups.length} · Занято: {formatSize(totalSize)}
                    </p>
                )}

                {loading && (
                    <div className={styles.state}><LoadingOutlined/> Загрузка...</div>
                )}
                {error && <div className={`${styles.state} ${styles.error}`}>{error}</div>}
                {!loading && !error && backups.length === 0 && (
                    <div className={styles.state}>Бэкапов пока нет</div>
                )}

                {!loading && !error && backups.length > 0 && (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                            <tr>
                                {!uuid && <th>Проект</th>}
                                <th>Файл</th>
                                <th>Размер</th>
                                <th>Дата</th>
                                <th>Комментарий</th>
                                <th>SFTP</th>
                                <th>SHA-256</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {backups.map((b) => (
                                <tr key={b.id}>
                                    {!uuid && (
                                        <td>
                                            <a className={styles.projectLink}
                                               onClick={() => navigate(`/projects/${b.project_uuid}/backups`)}>
                                                {b.project_name}
                                            </a>
                                        </td>
                                    )}
                                    <td className={styles.filename}>{b.filename}</td>
                                    <td>{formatSize(b.size_bytes)}</td>
                                    <td>{formatDate(b.created_at)}</td>
                                    <td>{b.comment || '—'}</td>
                                    <td>
                                        {b.sftp_synced_at ? (
                                            <span className={styles.synced} title={formatDate(b.sftp_synced_at)}>
                                                <CloudSyncOutlined/> Синхронизирован по SFTP
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td className={styles.hash} title={b.sha256}>{b.sha256.slice(0, 12)}…</td>
                                    <td className={styles.actions}>
                                        <a className={styles.iconButton} title="Скачать"
                                           href={backupsAPI.downloadUrl(b.project_uuid, b.id)}>
                                            <CloudDownloadOutlined/>
                                        </a>
                                        <button className={`${styles.iconButton} ${styles.danger}`} title="Удалить"
                                                disabled={deletingId === b.id} onClick={() => handleDelete(b)}>
                                            {deletingId === b.id ? <LoadingOutlined/> : <DeleteOutlined/>}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BackupsPage;
